import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../content/ssiUplineReport";
import { SsiEligibilityWindowExtended, SSIUplineResponse } from "../../../services/SSIUpline/ssiUplineAPI.types";
import { ErrorPage, ERROR_TYPE, IconType } from "../../ErrorPage/ErrorPage";
import EarnedPaymentTable, { GroupedEarnedPaymentRow } from "./EarnedPaymentTable/EarnedPaymentTable";
import { EarnedPaymentCard } from "./EarnedPaymentCard/EarnedPaymentCard";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import useWindowSize from "../../../common/hooks/useWindowSize";
import breakpoints from "../../../common/scss/breakpoints.module.scss";
import { track } from "../../../common/util/tealium";
import TEALIUM from "../../../common/enums/tealium-enums";
import { useAppDispatch } from "../../../store";
import { openProfileModal } from "../../../reducers/ssiUplineReport";
import { initialState } from "../../../reducers/ssiUplineReport";
import { getDisplayName } from "../../../common/util/displayName";
import "./EarnedPayment.scss";

interface Props {
  ssiUplineData: SSIUplineResponse;
  isLoading: boolean;
  selectedAboNumber: string;
}

export const EarnedPayment = ({ ssiUplineData, isLoading, selectedAboNumber }: Props) => {
  const { t } = useTranslation(["ssiUplineReport", "common"]);
  const { width } = useWindowSize();
  const scrollRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const isMobileView = Number(width) <= Number(breakpoints["mobileLarge"]);

  const handleProfileClick = (
    aboNumber: string,
    aff: string,
    sponsorNumber: string,
    elementId: string,
    eligibilityWindow: SsiEligibilityWindowExtended,
  ) => {
    dispatch(
      openProfileModal({
        aboNo: aboNumber,
        affNo: aff,
        sponsorNo: sponsorNumber,
        sponsorAff: aff,
        eligibilityWindow: eligibilityWindow,
        showEligibilityWindow: true,
        show: true,
        previousElementID: elementId,
      }),
    );
    track(TEALIUM.U_TAG_METHOD.LINK, "", "earned-payment", "SSI_Upline_Report", "", "ABO_profile");
  };

  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        scrollRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    }
  }, [selectedAboNumber]);

  const sortedGroups = [...(ssiUplineData?.EarnedPayment?.Groups || [])].sort(
    (a, b) => a.PaymentsReceived - b.PaymentsReceived,
  );

  const rows: GroupedEarnedPaymentRow[] = [];
  for (const group of sortedGroups) {
    rows.push({ type: "group", paymentCount: group.PaymentsReceived });
    for (const abo of group.ABOs ?? []) {
      rows.push({
        type: "data",
        data: {
          aff: abo.Aff,
          aboName: getDisplayName(abo.DisplayName, t),
          aboNumber: abo.Abo,
          programStartDate: abo.ProgramStartDate,
          paymentMonth: abo.PaymentMonth,
          sponsorName: getDisplayName(abo.SponsorDisplayName, t),
          sponsorNumber: abo.SponsorABO,
          isHighlighted: abo.Abo === selectedAboNumber,
          scrollRef: abo.Abo === selectedAboNumber,
          eligibilityWindow: abo.EligibilityWindow,
        },
      });
    }
  }

  const totalAbos = sortedGroups.reduce((sum, group) => sum + (group.ABOs ?? []).length, 0);

  if ((!ssiUplineData?.EarnedPayment || totalAbos === 0) && !isLoading) {
    return (
      <div className="EarnedPaymentSection__error">
        <ErrorPage
          errorType={ERROR_TYPE.NO_DOWNLINES}
          iconType={IconType.SearchCriteria}
          tealiumTrackFunction={() => track("view", "", "earned-payment_tab", "ssi_upline_report", "no_downlines", "")}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="EarnedPaymentSection">
        {isMobileView ? (
          <div className="EarnedPaymentSection__mobileCards">
            <div className="EarnedPaymentSection__groupTitle">
              <LeafRectangleLoader isLoading={true} width="40%" height="1.5rem" margin="0rem" />
            </div>
            <EarnedPaymentCard
              aboName=""
              aboNumber=""
              programStartDate=""
              paymentMonth=""
              sponsorName=""
              sponsorNumber=""
              isLoading={true}
            />
            <EarnedPaymentCard
              aboName=""
              aboNumber=""
              programStartDate=""
              paymentMonth=""
              sponsorName=""
              sponsorNumber=""
              isLoading={true}
            />
          </div>
        ) : (
          <div className="EarnedPaymentSection__tableWrapper">
            <EarnedPaymentTable rows={[]} isLoading={true} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="EarnedPaymentSection">
      {isMobileView ? (
        <div className="EarnedPaymentSection__mobileCards">
          {sortedGroups.map((group) => (
            <div key={group.PaymentsReceived}>
              <div className="EarnedPaymentSection__groupTitle">
                {group.PaymentsReceived}{" "}
                {group.PaymentsReceived === 1
                  ? t("earnedPayment.paymentReceived", defaultContent.earnedPayment.paymentReceived as string)
                  : t("earnedPayment.paymentsReceived", defaultContent.earnedPayment.paymentsReceived as string)}
              </div>
              {(group.ABOs ?? []).map((abo) => (
                <div
                  key={abo.Abo}
                  ref={abo.Abo === selectedAboNumber ? scrollRef : undefined}
                  className={`EarnedPaymentSection__cardWrapper ${abo.Abo === selectedAboNumber ? "EarnedPaymentSection__cardWrapper--highlighted" : ""}`}
                  aria-selected={abo.Abo === selectedAboNumber}
                >
                  <EarnedPaymentCard
                    aboName={getDisplayName(abo.DisplayName, t)}
                    aboNumber={abo.Abo}
                    programStartDate={abo.ProgramStartDate}
                    paymentMonth={abo.PaymentMonth}
                    sponsorName={getDisplayName(abo.SponsorDisplayName, t)}
                    sponsorNumber={abo.SponsorABO}
                    isLoading={false}
                    onProfileClick={() => {
                      handleProfileClick(
                        abo.Abo,
                        abo.Aff,
                        abo.SponsorABO,
                        `EarnedPaymentCardProfileButton${abo.Abo}`,
                        abo.EligibilityWindow,
                      );
                    }}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="EarnedPaymentSection__tableWrapper">
          <EarnedPaymentTable
            rows={rows}
            isLoading={false}
            onProfileClick={(aboNumber, aff, sponsorNumber, eligibilityWindow) => {
              handleProfileClick(
                aboNumber,
                aff,
                sponsorNumber,
                `EarnedPaymentTableProfileButton${aboNumber}`,
                eligibilityWindow,
              );
            }}
          />
        </div>
      )}
    </div>
  );
};
